import React, { useMemo, useState } from "react"
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
  Theme,
  TextField,
  InputAdornment,
  Tooltip
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useFormikContext } from "formik"
import { IApiResult } from "auth/interface/api.interface"
import { TemplateFormData, Vehicle, VehicleGroup } from "../CreateTemplateModal.interfaces"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch } from "@fortawesome/pro-light-svg-icons"
import { CheckTemplateSourceType, CheckTemplateListDto } from "../../../types/CheckTypes.interfaces"
import VehicleSwitch from "../VehicleSwitch"

type SelectionState = "none" | "some" | "all"

interface GroupChipButtonProps {
  label: string
  count: number
  selectionState: SelectionState
  onClick: () => void
  theme: Theme
}

const GroupChipButton: React.FC<GroupChipButtonProps> = ({
  label,
  count,
  selectionState,
  onClick,
  theme
}) => {
  let chipConfig = {
    backgroundColor: "",
    textColor: "",
    borderColor: "",
    borderWidth: 1
  }

  switch (selectionState) {
    case "all": // Fully selected - strong primary color
      chipConfig.backgroundColor = (theme.palette as any).secondary[200] || theme.palette.primary.main
      chipConfig.textColor = theme.palette.grey[700] || theme.palette.primary.dark
      chipConfig.borderColor = (theme.palette as any).secondary[500] || theme.palette.primary.dark
      chipConfig.borderWidth = 2
      break
    case "some": // Semi-selected - medium color with dashed border
      chipConfig.backgroundColor = (theme.palette as any).secondary[200] || theme.palette.primary.light
      chipConfig.textColor = theme.palette.grey[700] || theme.palette.primary.dark
      chipConfig.borderColor = (theme.palette as any).secondary[500] || theme.palette.primary.main
      chipConfig.borderWidth = 2
      break
    case "none":
    default: // Unselected - neutral colors
      chipConfig.backgroundColor = theme.palette.background.paper
      chipConfig.textColor = theme.palette.grey[700]
      chipConfig.borderColor = (theme.palette as any).secondary[200]
      chipConfig.borderWidth = 1
      break
  }

  return (
    <Chip
      label={`${label} (${count})`}
      onClick={onClick}
      variant="outlined"
      sx={{
        cursor: "pointer",
        borderRadius: "4px",
        fontWeight: 500,
        fontSize: 14,
        backgroundColor: chipConfig.backgroundColor,
        color: chipConfig.textColor,
        borderColor: chipConfig.borderColor,
        borderWidth: chipConfig.borderWidth,
        borderStyle: selectionState === "some" ? "dashed" : "solid",
        "&:hover": {
          backgroundColor: selectionState === "none"
            ? theme.palette.action.hover
            : chipConfig.backgroundColor,
          opacity: selectionState === "none" ? 1 : 0.9,
        },
      }}
    />
  )
}

interface VehicleSelectionStepProps {
  vehicles: Vehicle[]
  vehiclesResult: IApiResult
  vehicleGroups: VehicleGroup[]
  showInternalNumber: boolean
  setShowInternalNumber: (show: boolean) => void
  sessionServiceValues: any
  setSessionServiceValues: React.Dispatch<React.SetStateAction<any>>
  originalServiceValues: any
  getGroupSelectionState: (group: VehicleGroup, selectedVehicles: number[]) => "all" | "some" | "none"
  toggleGroupSelection: (group: VehicleGroup, formik: any) => void
  checkTemplateListResult: IApiResult
  checkTemplate?: any
}

export const VehicleSelectionStep: React.FC<VehicleSelectionStepProps> = ({
  vehicles,
  vehiclesResult,
  vehicleGroups,
  showInternalNumber,
  setShowInternalNumber,
  sessionServiceValues,
  setSessionServiceValues,
  originalServiceValues,
  getGroupSelectionState,
  toggleGroupSelection,
  checkTemplateListResult,
  checkTemplate
}) => {
  const theme = useTheme()
  const formik = useFormikContext<TemplateFormData>()
  const { t } = useTranslation()
  const [vehicleSearch, setVehicleSearch] = useState("");

  const { disabledVehicleIds, vehicleTemplateMap } = useMemo(() => {
    if (!formik.values.usesInspection) {
      return { disabledVehicleIds: [], vehicleTemplateMap: {} }
    }

    if (!checkTemplateListResult?.data || checkTemplateListResult.status !== 2) {
      return { disabledVehicleIds: [], vehicleTemplateMap: {} }
    }

    const disabledIds = new Set<number>()
    const templateMap: Record<number, string[]> = {}

    checkTemplateListResult.data.forEach((template: CheckTemplateListDto) => {
      if (checkTemplate && template.id === checkTemplate.id) return

      const hasTransportStyrelsanInterval = template.checkTemplateIntervals?.some(
        (interval) => interval.sourceType === CheckTemplateSourceType.TransportStyrelsen
      )

      if (hasTransportStyrelsanInterval) {
        template.vehicleIds?.forEach((vehicleId) => {
          disabledIds.add(vehicleId)
          if (!templateMap[vehicleId]) {
            templateMap[vehicleId] = []
          }
          templateMap[vehicleId].push(template.name)
        })
      }
    })

    return {
      disabledVehicleIds: Array.from(disabledIds),
      vehicleTemplateMap: templateMap
    }
  }, [formik.values.usesInspection, checkTemplateListResult, checkTemplate])

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.toLowerCase().trim();

    if (!q) return vehicles;

    return vehicles.filter((v) =>
      [
        v.regNumber,
        v.internnumber,
        v.brand,
        v.model,
      ]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    );
  }, [vehicles, vehicleSearch]);


  if (vehiclesResult.status === 1) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
        <Typography variant="bodySmall" color="textSecondary">
          {t("global.validation.loading_vehicles", "Loading vechiles")}...
        </Typography>
      </Box>
    )
  }

  if (vehiclesResult.status === 3) {
    return (
      <Box>
        <Alert variant="outlined" severity="error" sx={{ mb: 2 }}>
          {t("global.validation.failed_loading_vehicles", "Loading vehicles failed")}{vehiclesResult.data?.messages.join(", ")}
        </Alert>
      </Box>
    )
  }

  return (
    <Box display="flex" flexDirection="column" sx={{ overflow: { xs: "scroll", md: "hidden" } }} height="100%">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexDirection={{ xs: "column", sm: "row" }} flexWrap="wrap">
        <Typography variant="h3">
          {t("global.validation.choose_included_vehicles", "Choose vehicles to include")}
        </Typography>
        <VehicleSwitch showValue={showInternalNumber} setShowValue={setShowInternalNumber} />
      </Box>

      {vehicleGroups.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box mb={1}>
            <Typography variant="labelLarge">
              {t("global.vehicle_groups", "Vehicle groups")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {vehicleGroups.map((group) => {
              const selectionState = getGroupSelectionState(group, formik.values.selectedVehicles)
              return (
                <GroupChipButton
                  key={group.id}
                  label={group.name}
                  count={group.vehicleIds.length}
                  selectionState={selectionState}
                  onClick={() => toggleGroupSelection(group, formik)}
                  theme={theme}
                />
              )
            })}
          </Box>
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}

      <Grid container justifyContent="space-between" alignItems="center" gap={1} mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            sx={{ minWidth: "120px" }}
            control={
              <Checkbox
                sx={{
                  color: (theme.palette as any).primary[800],
                  "&.Mui-checked": {
                    color: (theme.palette as any).primary[800]
                  },
                  "&.MuiCheckbox-indeterminate": {
                    color: (theme.palette as any).primary[800],
                  },
                }}
                indeterminate={
                  formik.values.selectedVehicles.length > 0 &&
                  formik.values.selectedVehicles.length < vehicles.filter(v => !disabledVehicleIds.includes(v.id)).length
                }
                checked={
                  vehicles.filter(v => !disabledVehicleIds.includes(v.id)).length > 0 &&
                  formik.values.selectedVehicles.length === vehicles.filter(v => !disabledVehicleIds.includes(v.id)).length
                }
                onChange={async (e) => {
                  if (e.target.checked) {
                    // When selecting all, restore any preserved values (excluding disabled vehicles)
                    const selectableVehicles = vehicles.filter(v => !disabledVehicleIds.includes(v.id))
                    await formik.setFieldValue("selectedVehicles", selectableVehicles.map(v => v.id), true)
                    await formik.setFieldTouched("selectedVehicles", true, false)
                    selectableVehicles.forEach(vehicle => {
                      // Always initialize/restore values for selected vehicles
                      const currentValues = formik.values.vehicleLastValues[vehicle.id]
                      const sessionValues = sessionServiceValues[vehicle.id]
                      const originalValues = originalServiceValues[vehicle.id]
                      const restoredValues = currentValues || sessionValues || originalValues || {}

                      formik.setFieldValue(`vehicleLastValues.${vehicle.id}`, restoredValues)
                    })
                  } else {
                    // When deselecting all, preserve all current values
                    vehicles.forEach(vehicle => {
                      if (formik.values.vehicleLastValues[vehicle.id]) {
                        setSessionServiceValues((prev: any) => ({
                          ...prev,
                          [vehicle.id]: formik.values.vehicleLastValues[vehicle.id]
                        }))
                      }
                    })
                    await formik.setFieldValue("selectedVehicles", [], true)
                    await formik.setFieldTouched("selectedVehicles", true, false)
                  }
                }}
              />
            }
            label={t("global.choose_all", "Choose all")}
          />
          <TextField
            fullWidth
            size="small"
            placeholder={t("global.search_vehicle", "Search vehicle")}
            value={vehicleSearch}
            onChange={(e) => setVehicleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} />
                </InputAdornment>
              ),
            }}
          />
        </Box>


        <Box display="flex" mr={2}>
          <Typography variant="h5">
            {formik.values.selectedVehicles.length}
          </Typography>
          <Typography component="span">
            &nbsp;
            {t("global.vehcles_picked", "Vehicles picked")?.toLowerCase()}
          </Typography>
        </Box>
      </Grid>

      <Box sx={{ flexGrow: 1, overflow: "auto", minHeight: 0 }}>
        <Grid container spacing={2}>
          {filteredVehicles.map((vehicle) => {
            const isDisabled = disabledVehicleIds.includes(vehicle.id)
            const templateNames = vehicleTemplateMap[vehicle.id] || []
            const tooltipTitle = isDisabled
              ? `${t("global.used_in_template", "Used in template")}: ${templateNames.join(", ")}`
              : ""

            return (
              <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                <Tooltip title={tooltipTitle} arrow placement="top">
                  <Paper
                    sx={{
                      p: 2,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      boxShadow: "none",
                      borderRadius: "8px",
                      border: formik.values.selectedVehicles.includes(vehicle.id)
                        ? `2px solid ${(theme.palette as any).primary[800]}`
                        : `1px solid ${theme.palette.divider}`,
                      backgroundColor: isDisabled
                        ? theme.palette.action.disabledBackground
                        : formik.values.selectedVehicles.includes(vehicle.id)
                          ? theme.palette.primary.light + "20"
                          : "transparent",
                      opacity: isDisabled ? 0.6 : 1,
                      "&:hover": {
                        backgroundColor: isDisabled ? theme.palette.action.disabledBackground : theme.palette.action.hover,
                      },
                    }}
                    onClick={async () => {
                      if (isDisabled) return
                      if (formik.values.selectedVehicles.includes(vehicle.id)) {
                        // When unchecking, preserve current values to session
                        if (formik.values.vehicleLastValues[vehicle.id]) {
                          setSessionServiceValues((prev: any) => ({
                            ...prev,
                            [vehicle.id]: formik.values.vehicleLastValues[vehicle.id]
                          }))
                        }
                        await formik.setFieldValue("selectedVehicles",
                          formik.values.selectedVehicles.filter((id: any) => id !== vehicle.id), true
                        )
                        await formik.setFieldTouched("selectedVehicles", true, false)
                      } else {
                        // When checking, restore values from session or original
                        await formik.setFieldValue("selectedVehicles",
                          [...formik.values.selectedVehicles, vehicle.id], true
                        )
                        await formik.setFieldTouched("selectedVehicles", true, false)
                        // Always initialize/restore values for selected vehicle
                        const currentValues = formik.values.vehicleLastValues[vehicle.id]
                        const sessionValues = sessionServiceValues[vehicle.id]
                        const originalValues = originalServiceValues[vehicle.id]
                        const restoredValues = currentValues || sessionValues || originalValues || {}

                        formik.setFieldValue(`vehicleLastValues.${vehicle.id}`, restoredValues)
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={formik.values.selectedVehicles.includes(vehicle.id)}
                        disabled={isDisabled}
                        onChange={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0, mr: 1 }}
                      />
                      <Typography variant="bodySmall" fontWeight="bold">
                        {showInternalNumber
                          ? (vehicle.internnumber || t("global.no_intern_number", "No intern number"))
                          : vehicle.regNumber
                        }
                      </Typography>
                    </Box>
                    <Typography variant="bodySmall" color="textSecondary">
                      {vehicle.brand} {vehicle.model}
                    </Typography>
                  </Paper>
                </Tooltip>
              </Grid>
            )
          })}
        </Grid>
      </Box>

      {formik.touched.selectedVehicles && formik.errors.selectedVehicles && (
        <Alert variant="outlined" severity="error" sx={{ mt: 2 }}>
          {formik.errors.selectedVehicles}
        </Alert>
      )}
    </Box>
  )
}
